/* Feb-2026 · Word-Credit Balance Pill for the top-nav.
 *
 * Shows the user's remaining PAYG word credits for the two premium
 * models gated on the Studio tier (Opus 4.7, GPT-5.4). Hidden when
 * balance is zero across all models (users who never bought credits
 * don't need to see the pill). Click opens the buy dialog.
 */
import React, { useEffect, useState } from 'react';
import { Coins } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const POLL_MS  = 30_000;

async function fetchBalance() {
  const r = await fetch(`${API_BASE}/api/word-credits/balance`, { credentials: 'include' });
  if (!r.ok) return null;
  return r.json();
}

function fmtK(n) {
  if (!n) return '0';
  if (n < 1000) return `${n}`;
  return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}K`;
}

export default function WordCreditBalancePill({ onOpenBuy }) {
  const [bal, setBal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const r = await fetchBalance();
      if (!cancelled) setBal(r);
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!bal || !bal.balances_by_model) return null;
  const opus  = bal.balances_by_model['claude-opus-4-7'] || 0;
  const gpt54 = bal.balances_by_model['gpt-5-4'] || 0;
  if (opus + gpt54 === 0) return null;

  const total = opus + gpt54;
  const tone =
    total >= 100_000 ? 'ok'
    : total >= 20_000 ? 'warn'
    : 'low';

  const toneClass = {
    ok:   'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800/40',
    warn: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800/40',
    low:  'bg-red-100 text-red-900 border-red-300 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800/40',
  }[tone];

  return (
    <button
      type="button"
      onClick={onOpenBuy}
      title={`Word credits · Opus: ${fmtK(opus)} · GPT-5.4: ${fmtK(gpt54)}. Click to buy more.`}
      data-testid="word-credit-balance-pill"
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-full border px-2 font-mono-stat text-[11px] transition-colors',
        toneClass, 'hover:brightness-105',
      )}
    >
      <Coins className="h-3 w-3" />
      {fmtK(total)} <span className="opacity-70">credits</span>
    </button>
  );
}
